import { useEffect, useRef } from 'react';

import { Text } from '@mantine/core';
import { useLocalStorage, useMounted } from '@mantine/hooks';

import { CompanyProfile, QrScan, QrScanSource, QrScanType, UserReferrer } from 'models';
import { QrScanHelpers } from 'helpers/QrScanHelpers';
import { UserHelpers } from 'helpers/UserHelpers';

import { UserInfo, UserInfoModal } from 'components/showtime/components/UserInfoModal';
import { useScanEvent } from 'components/showtime/hooks/use-scan-event';
import { useSavedProfiles } from 'components/showtime/hooks/useSavedProfiles';
import { OpenScan } from 'components/showtime/state/scan-sheet';
import { useCurrentUser } from 'hooks/use-current-user';
import { useLocalUserInfo } from 'hooks/use-local-user-info';
import { AuthenticationService } from 'services/AuthenticationService';
import { InternalTrackingService } from 'services/InternalTrackingService';
import { RouterService } from 'services/RouterService';
import { ShowtimeService } from 'services/ShowtimeService';

export type SaveScanFlowProps = {
    company: CompanyProfile;
    type: QrScanType;
    itemId: string;
    title: string;
    source: QrScanSource;
    onScanSaving: (saving: Pick<OpenScan, 'scan' | 'retry'>) => void;
    // Only saves from the website can be cancelled; a QR scan always asks who the visitor is
    onCancel?: () => void;
    // An exhibitor trying their own code sees every step, but nothing is saved, sent or tracked
    test?: boolean;
};

// Saves a profile, brochure or product to RE+ Showtime, asking for their name and email first if we do not know them
const SaveScanFlow = ({ company, type, itemId, title, source, onScanSaving, onCancel, test }: SaveScanFlowProps) => {
    const mounted = useMounted();
    const isQrScan = source === QrScanSource.QR_CODE;

    const user = useCurrentUser();
    const { type: userType } = useLocalUserInfo();
    const { event, isLoading: eventLoading } = useScanEvent(company);
    const { qrScans, isLoading: savedProfilesLoading } = useSavedProfiles();

    const [_email, setEmail] = useLocalStorage<string | undefined>({ key: UserHelpers.localStorageKey.email });
    const [_name, setName] = useLocalStorage<string | undefined>({ key: UserHelpers.localStorageKey.name });

    const email = _email !== 'undefined' ? _email : undefined;
    const name = _name !== 'undefined' ? _name : undefined;

    // localStorage is only read after mount, so before that every visitor looks unknown
    const knownVisitor = !test && mounted && Boolean(user || (email && name));
    const eventsLoading = eventLoading || savedProfilesLoading;

    const saveStarted = useRef(false);

    const saveScan = (userInfo?: QrScan['userInfo']) => {
        if (saveStarted.current) {
            return;
        }

        saveStarted.current = true;

        // A visitor saves each profile, brochure or product once per event; scanning its code again brings back their notes
        const existingSave = QrScanHelpers.findLatestScan(
            qrScans.filter((scan) => scan.event === event?.id),
            type,
            itemId,
        );

        if (existingSave) {
            onScanSaving({ scan: existingSave });
        } else {
            startSave(userInfo);
        }

        if (isQrScan) {
            // Without the flag, a reload or a shared link does not count as another scan
            RouterService.removeQuery(QrScanHelpers.gateParam, 'replace');
        }
    };

    const startSave = (userInfo?: QrScan['userInfo']) => {
        const payload: Partial<QrScan> = {
            type,
            source,
            company: company.id,
            event: event?.id,
            collateral: type === QrScanType.COLLATERAL ? itemId : undefined,
            product: type === QrScanType.PRODUCT ? itemId : undefined,
        };

        if (user) {
            payload.user = user.id;
        } else {
            payload.userInfo = userInfo ?? { email, name };
        }

        // The visitor came for the item, so a failed save must not keep them from it; the sheet offers a retry
        const scan = ShowtimeService.createQrScan(payload, company).catch((error) => {
            console.error('Could not save to Showtime', error);

            return undefined;
        });

        onScanSaving({ scan, retry: () => startSave(userInfo) });
    };

    const viewTracked = useRef(false);

    // Same event as profile scans in Showtime, so every QR landing is counted in one place
    useEffect(() => {
        if (test || !isQrScan || !mounted || eventsLoading || viewTracked.current) {
            return;
        }

        viewTracked.current = true;

        InternalTrackingService.track('qrScan.view', {
            type,
            item: itemId,
            company: company.id,
            email,
            name,
            event: event?.id,
        });
    }, [mounted, eventsLoading]);

    useEffect(() => {
        if (knownVisitor && !eventsLoading) {
            saveScan();
        }
    }, [knownVisitor, eventsLoading]);

    const onSubmitUserInfo = async ({ createAccount, ...userInfo }: UserInfo) => {
        if (test) {
            onScanSaving({
                scan: { id: 'test', company: company.id, type, source, createdAt: new Date().toISOString() },
            });
            await RouterService.removeQuery(QrScanHelpers.gateParam, 'replace');

            return;
        }

        if (createAccount) {
            await AuthenticationService.sendMagicTokenLogin(userInfo.email, {
                referrer: UserReferrer.REPLUS,
                type: userType,
            });
        }

        setEmail(userInfo.email);
        setName(userInfo.name);

        saveScan(userInfo);
    };

    return (
        <UserInfoModal
            opened={mounted && !knownVisitor}
            defaultValues={{ name, email, createAccount: true }}
            onSubmit={onSubmitUserInfo}
            onClose={onCancel}
            intro={
                <Text fz="sm">
                    {type === QrScanType.PROFILE ? (
                        <>
                            Tell us who you are to save <strong>{company.name}</strong> to RE+ Showtime and keep notes
                            from your visit. They will see that you stopped by, so they can follow up.
                        </>
                    ) : isQrScan ? (
                        <>
                            Tell us who you are to open <strong>{title}</strong>. {company.name} will see that you
                            scanned it, so they can follow up.
                        </>
                    ) : (
                        <>
                            Tell us who you are to save <strong>{title}</strong> to RE+ Showtime. {company.name} will
                            see that you saved it, so they can follow up.
                        </>
                    )}
                </Text>
            }
        />
    );
};

export { SaveScanFlow };
